/**
 * Phase 16F Administration → Notifications (REQ-NTF-06): the roles that receive each critical
 * trigger and the missed clock-in grace period (15–60 min, default 30). Critical triggers cannot
 * be switched off, so there is no off switch and every trigger keeps at least one role.
 * Permission: admin:notifications:settings:update (owner, admin). Changes are audit-logged server-side.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader } from 'lucide-react';
import NotificationService from '../../services/notifications/notification.service';
import { CODE_LABELS, NotificationSettingsModel } from '../../dtos/notifications/notification.mapper';
import {
  MISSED_CLOCK_IN_GRACE_MAX,
  MISSED_CLOCK_IN_GRACE_MIN,
  NOTIFICATION_CODES,
} from '../../dtos/notifications/notification.dto';
import { useCanPerform } from '../../hooks/useCanPerform';
import { useToast } from '../common/Toast';

export interface SettingsErrors {
  grace?: string;
  roles?: Record<string, string>;
}

/** Grace is a whole number of minutes in 15–60; every trigger keeps at least one role. */
export function validateSettings(roles: Record<string, string[]>, graceText: string): SettingsErrors {
  const errors: SettingsErrors = {};
  const trimmed = graceText.trim();
  const grace = Number(trimmed);
  if (!/^\d+$/.test(trimmed) || !Number.isInteger(grace) || grace < MISSED_CLOCK_IN_GRACE_MIN || grace > MISSED_CLOCK_IN_GRACE_MAX) {
    errors.grace = `Enter a whole number of minutes from ${MISSED_CLOCK_IN_GRACE_MIN} to ${MISSED_CLOCK_IN_GRACE_MAX}.`;
  }
  const roleErrors: Record<string, string> = {};
  for (const [code, list] of Object.entries(roles)) {
    if (!list.length) roleErrors[code] = 'Choose at least one role. Critical alerts cannot be switched off.';
  }
  if (Object.keys(roleErrors).length) errors.roles = roleErrors;
  return errors;
}

const sameSettings = (a: Record<string, string[]>, b: Record<string, string[]>) =>
  Object.keys({ ...a, ...b }).every((k) => [...(a[k] || [])].sort().join(',') === [...(b[k] || [])].sort().join(','));

const NotificationSettings: React.FC = () => {
  const canEdit = useCanPerform('manage:notificationsettings');
  const { showToast } = useToast();
  const [saved, setSaved] = useState<NotificationSettingsModel | null>(null);
  const [roles, setRoles] = useState<Record<string, string[]>>({});
  const [graceText, setGraceText] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [errors, setErrors] = useState<SettingsErrors>({});

  const apply = (model: NotificationSettingsModel) => {
    setSaved(model);
    setRoles(model.roles);
    setGraceText(String(model.missedClockInGraceMinutes));
  };

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      apply(await NotificationService.getSettings());
    } catch (err) {
      setLoadError(err instanceof Error && err.message ? err.message : 'Could not load notification settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const dirty = useMemo(() => !!saved && (!sameSettings(saved.roles, roles) || graceText.trim() !== String(saved.missedClockInGraceMinutes)), [saved, roles, graceText]);

  const toggleRole = (code: string, role: string) => {
    setRoles((prev) => {
      const list = prev[code] || [];
      return { ...prev, [code]: list.includes(role) ? list.filter((r) => r !== role) : [...list, role] };
    });
    setErrors((prev) => (prev.roles?.[code] ? { ...prev, roles: { ...prev.roles, [code]: '' } } : prev));
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!saved) return;
    const found = validateSettings(roles, graceText);
    setErrors(found);
    if (found.grace || found.roles) return;
    setSaving(true);
    setSaveError(null);
    try {
      const next = await NotificationService.updateSettings({ ...saved, roles, missedClockInGraceMinutes: Number(graceText.trim()) });
      apply(next);
      showToast('Notification settings saved.', { variant: 'success' });
    } catch (err) {
      setSaveError(err instanceof Error && err.message ? err.message : 'Could not save notification settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6 flex items-center gap-2 text-sm text-slate-500" role="status">
        <Loader size={18} className="animate-spin" aria-hidden="true" /> Loading notification settings
      </div>
    );
  }
  if (loadError || !saved) {
    return (
      <div className="bg-white rounded-lg shadow p-6" role="alert">
        <p className="text-sm text-red-700 flex items-center gap-1.5"><AlertTriangle size={16} aria-hidden="true" /> {loadError || 'Could not load notification settings'}</p>
        <button type="button" onClick={load} className="mt-3 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">Try again</button>
      </div>
    );
  }

  return (
    <form onSubmit={save} noValidate className="bg-white rounded-lg shadow p-4 md:p-6 space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Critical alert notifications</h3>
        <p className="text-sm text-gray-600 mt-1">
          Choose who receives each critical alert in the app and by email. Critical alerts can't be switched off.
          People receive alerts only for patients they can already see; clinicians only for their assigned patients.
          The summary author always receives discharge summary alerts.
        </p>
      </div>

      <div>
        <label htmlFor="missed-clock-in-grace" className="block text-sm font-medium text-gray-700 mb-1">Missed clock-in grace period (minutes)</label>
        <input
          id="missed-clock-in-grace"
          type="number"
          inputMode="numeric"
          min={MISSED_CLOCK_IN_GRACE_MIN}
          max={MISSED_CLOCK_IN_GRACE_MAX}
          step={1}
          value={graceText}
          onChange={(e) => { setGraceText(e.target.value); setErrors((prev) => ({ ...prev, grace: undefined })); }}
          disabled={!canEdit}
          className={`w-32 px-3 py-2 border rounded-lg focus:ring-2 ${errors.grace ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`}
          aria-invalid={!!errors.grace}
          aria-describedby={`missed-clock-in-grace-hint${errors.grace ? ' missed-clock-in-grace-err' : ''}`}
        />
        <p id="missed-clock-in-grace-hint" className="text-xs text-gray-500 mt-1">
          Alert when no visit has started this long after a scheduled shift began ({MISSED_CLOCK_IN_GRACE_MIN}–{MISSED_CLOCK_IN_GRACE_MAX}).
        </p>
        {errors.grace && <p id="missed-clock-in-grace-err" className="text-sm text-red-600 mt-1">{errors.grace}</p>}
      </div>

      <div className="space-y-4">
        {NOTIFICATION_CODES.map((code) => {
          const list = roles[code] || [];
          const err = errors.roles?.[code];
          return (
            <fieldset key={code} className="border border-gray-200 rounded-lg p-3" aria-describedby={err ? `roles-${code}-err` : undefined}>
              <legend className="px-1 text-sm font-medium text-gray-900">{CODE_LABELS[code] || code}</legend>
              <div className="flex flex-wrap gap-x-4 gap-y-2 mt-1">
                {saved.availableRoles.map((role) => (
                  <label key={role.key} className="inline-flex items-center gap-2 text-sm text-gray-700 min-h-[32px]">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300"
                      checked={list.includes(role.key)}
                      onChange={() => toggleRole(code, role.key)}
                      disabled={!canEdit}
                    />
                    {role.label}
                  </label>
                ))}
              </div>
              {err && <p id={`roles-${code}-err`} className="text-sm text-red-600 mt-1">{err}</p>}
            </fieldset>
          );
        })}
      </div>

      {saveError && <p className="text-sm text-red-600" role="alert">{saveError}</p>}

      {canEdit && (
        <div className="flex justify-end gap-2">
          <button type="button" className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-60" onClick={() => { apply(saved); setErrors({}); setSaveError(null); }} disabled={!dirty || saving}>
            Discard changes
          </button>
          <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60" disabled={!dirty || saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
    </form>
  );
};

export default NotificationSettings;
